//
// Copyright 2021 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Button from "@material-ui/core/Button";
import MenuItem from "@material-ui/core/MenuItem";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import CloseableDialog from "eai-configurator-ui/components/configuration/dialog/CloseableDialog";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import * as Utils from "eai-configurator-ui/components/Utils";
import MessageDialog, {
  useMessageDialog,
} from "eai-configurator-ui/components/configuration/dialog/MessageDialog";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  textField: {
    width: "100%",
  },
  comboBox: {
    paddingTop: 10,
  },
  error: {
    color: "red",
  },
  checkbox: {
    paddingLeft: 0,
  },
}));

const DataSourceEditDialog = ({
  open,
  handleSuccess,
  handleClose,
  dataSource: inputDataSource,
}) => {
  const classes = useStyles();
  const [dataSource, setDataSource] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useMessageDialog();
  const [isError, setIsError] = React.useState(false);

  React.useEffect(() => {
    let dataSource = inputDataSource;
    if (!dataSource) {
      dataSource = {};
    }
    setDataSource({ ...dataSource });
  }, [inputDataSource]);

  const setDataSourceProperty = (event, propertyName) => {
    const dataSourceClone = { ...dataSource };
    dataSourceClone[propertyName] = event.target.value;
    setDataSource(dataSourceClone);
  };

  const setDataSourceName = (event) => {
    setDataSourceProperty(event, "name");
  };
  const setDataSourceUrl = (event) => {
    setDataSourceProperty(event, "url");
  };
  const setDataSourceUsername = (event) => {
    setDataSourceProperty(event, "username");
  };
  const setDataSourcePassword = (event) => {
    setDataSourceProperty(event, "password");
  };

  const isEditingExistingDataSource = () => {
    return inputDataSource;
  };

  const handleApply = () => {
    setSubmitted(true);
    const params = {
      name: dataSource.name,
      url: dataSource.url,
      username: dataSource.username,
      password: dataSource.password,
    };
    if (inputDataSource) {
      params["id"] = inputDataSource.id;
    }
    Utils.fetchData(
      "loader/dataSource",
      {
        method: "post",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: Utils.createFormBody(params),
      },
      false,
      false
    )
      .catch((e) => {
        setMessage("Data Source edit failed");
        setIsError(true);
        setSubmitted(false);
      })
      .then((response) => {
        if (response && response.ok) {
          handleClose();
          setSubmitted(false);
          handleSuccess(dataSource);
        } else {
          setMessage("Data Source edit failed");
          setIsError(true);
          setSubmitted(false);
        }
      });
  };

  const handleTestConnectionClick = () => {
    if (testing) {
      return;
    }
    setTesting(true);
    const params = {
      name: dataSource.name,
      url: dataSource.url,
      username: dataSource.username,
      password: dataSource.password,
    };
    Utils.fetchData(
      "loader/testDataSource",
      {
        method: "post",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: Utils.createFormBody(params),
      },
      false,
      false
    )
      .then((response) => {
        setMessage("Connection successful!");
        setIsError(false);
        setTesting(false);
      })
      .catch((response) => {
        Utils.getErrorMessage(response, "Connection failed.").then(setMessage);
        setIsError(true);
        setTesting(false);
      });
  };

  const isEditing = isEditingExistingDataSource();
  return (
    <div>
      <MessageDialog {...message} error={isError} success={!isError} />
      <CloseableDialog
        title={isEditing ? "Edit Data Source" : "Add Data Source"}
        open={open}
        maxWidth="sm"
        fullWidth
        handleClose={handleClose}
      >
        <ValidatorForm onSubmit={handleApply} style={{ width: "100%" }}>
          <DialogContent dividers>
            <TextValidator
              className={classes.textField}
              label="Data Source Name"
              type="text"
              variant="standard"
              value={dataSource.name}
              onChange={setDataSourceName}
              validators={["required"]}
              errorMessages={["This field is required"]}
            />
            <TextValidator
              className={classes.textField}
              label="JDBC URL"
              type="text"
              variant="standard"
              value={dataSource.url}
              onChange={setDataSourceUrl}
              id="url"
              helperText={
                <div>
                  The JDBC URL depends on the database selected:<br/>
                  For Oracle, use URL format: jdbc:oracle:thin:@HOST:PORT:SID;<br/>
                  For SQL Server, use URL format: jdbc:sqlserver://HOST:PORT;database=DATABASE;
                </div>
              }
              validators={["required"]}
              errorMessages={["This field is required"]}
            />
            <TextValidator
              className={classes.textField}
              label="Username"
              type="text"
              variant="standard"
              value={dataSource.username}
              onChange={setDataSourceUsername}
              id="username"
              validators={["required"]}
              errorMessages={["This field is required"]}
            />
            <TextValidator
              className={classes.textField}
              label="Password"
              type="password"
              variant="standard"
              autoComplete="new-password"
              value={dataSource.password}
              onChange={setDataSourcePassword}
              validators={["required"]}
              errorMessages={["This field is required"]}
            />
          </DialogContent>
          <DialogActions>
            <Button
              type="test"
              color="primary"
              disabled={testing}
              onClick={handleTestConnectionClick}
            >
              {testing ? "Testing..." : "Test Connection"}
            </Button>
            <Button type="submit" color="primary" disabled={submitted}>
              {submitted ? "Applying..." : "Apply"}
            </Button>
          </DialogActions>
        </ValidatorForm>
      </CloseableDialog>
    </div>
  );
};
export default DataSourceEditDialog;
